import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { wishAPI } from '../api';

const WishesContext = createContext();

export const useWishes = () => {
    const context = useContext(WishesContext);
    if (!context) {
        throw new Error('useWishes must be used within a WishesProvider');
    }
    return context;
};

export const WishesProvider = ({ children }) => {
    const [wishes, setWishes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    // Read current wishes/lastFetched through refs so fetchWishes keeps a
    // stable identity and consumers' effects don't re-run on every fetch.
    const wishesRef = useRef(wishes);
    wishesRef.current = wishes;
    const lastFetchedRef = useRef(lastFetched);
    lastFetchedRef.current = lastFetched;

    const fetchWishes = useCallback(async (force = false) => {
        // If we already have wishes and it's been less than 5 minutes, don't refetch
        if (!force && wishesRef.current.length > 0 && lastFetchedRef.current) {
            const fiveMinutes = 5 * 60 * 1000;
            if (Date.now() - lastFetchedRef.current < fiveMinutes) {
                return wishesRef.current;
            }
        }

        setLoading(true);
        setError(null);
        try {
            const response = await wishAPI.getWishes();
            const wishesData = response.data?.results || response.data || [];
            setWishes(Array.isArray(wishesData) ? wishesData : []);
            setLastFetched(Date.now());
            return wishesData;
        } catch (err) {
            console.error('Failed to fetch wishes:', err);
            setError(err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshWishes = useCallback(() => {
        return fetchWishes(true);
    }, [fetchWishes]);

    const value = useMemo(() => ({
        wishes,
        loading,
        error,
        fetchWishes,
        refreshWishes,
        lastFetched
    }), [wishes, loading, error, fetchWishes, refreshWishes, lastFetched]);

    return (
        <WishesContext.Provider value={value}>
            {children}
        </WishesContext.Provider>
    );
};
